import { useState } from "react";
import type { Tariff } from "@spf/contracts";
import { api } from "../../lib/client.js";
import { useAsync } from "../../lib/useAsync.js";
import { useTariffs } from "../../lib/usePages.js";
import { formatAmount, formatLocalDate } from "../../lib/format.js";
import {
  Empty,
  Failed,
  LoadOlder,
  Loading,
  buttonClass,
  secondaryButtonClass,
} from "../ViewState.js";
import { AddTariff } from "./AddTariff.js";

/**
 * The tariff table: what a kWh from the grid costs, from which date.
 *
 * Everyone with `tariffs:read` sees the rates, because a viewer sees cost and
 * should be able to tell what it was priced at. `tariffs:write` adds the form
 * and the remove buttons; the endpoints enforce the same permission, so hiding
 * them is presentation only.
 *
 * A rate is never edited in place. A new rate starts on a date and ends the
 * one before it there; a mistake is removed and entered again. Both re-price
 * the days they cover, and both are audited (docs/DECISIONS/0033).
 */
export function TariffsPanel({ canWrite }: { canWrite: boolean }) {
  const { state, loadingMore, moreFailed, reload, loadMore } = useTariffs();
  const { state: site } = useAsync(api.site, "Could not load site details.");
  const currency = site.kind === "ready" ? site.value.currency : "—";
  const [status, setStatus] = useState("");

  const changed = (verb: string) => (tariff: Tariff) => {
    setStatus(`The rate from ${formatLocalDate(tariff.effectiveFrom)} was ${verb}.`);
    reload();
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-prose text-sm text-ink-muted">
        Every cost in this app is priced from these rates, each quarter hour at the rate in force
        when it started. Adding a rate with an earlier date, or removing one, re-prices the days it
        covers.
      </p>

      {state.kind === "loading" && <Loading label="Loading tariffs…" />}
      {state.kind === "error" && <Failed message={state.message} onRetry={reload} />}
      {state.kind === "ready" && state.items.length === 0 && <NoTariffs canWrite={canWrite} />}
      {state.kind === "ready" && state.items.length > 0 && (
        <TariffList
          tariffs={state.items}
          more={{ available: state.nextBefore !== null, loading: loadingMore, failed: moreFailed }}
          onLoadMore={loadMore}
          currency={currency}
          canWrite={canWrite}
          onRemoved={changed("removed")}
        />
      )}

      {canWrite && (
        <AddTariff
          onAdded={changed("added")}
          onEdited={() => {
            setStatus("");
          }}
        />
      )}

      {/* Outside the list, which passes back through loading on every change
          and would take the confirmation with it. */}
      <p role="status" className="text-sm text-ink-muted">
        {status}
      </p>
    </div>
  );
}

function NoTariffs({ canWrite }: { canWrite: boolean }) {
  return (
    <Empty>
      No tariff has been entered, so no cost is shown anywhere.{" "}
      {canWrite
        ? "Add the rate from your electricity bill below."
        : "An administrator can add one."}
    </Empty>
  );
}

function TariffList({
  tariffs,
  more,
  onLoadMore,
  currency,
  canWrite,
  onRemoved,
}: {
  tariffs: readonly Tariff[];
  more: { available: boolean; loading: boolean; failed: boolean };
  onLoadMore: () => void;
  currency: string;
  canWrite: boolean;
  onRemoved: (tariff: Tariff) => void;
}) {
  return (
    <>
      <ul className="flex flex-col gap-3">
        {tariffs.map((tariff) => (
          <TariffRow
            key={tariff.id}
            tariff={tariff}
            currency={currency}
            canWrite={canWrite}
            onRemoved={() => {
              onRemoved(tariff);
            }}
          />
        ))}
      </ul>
      {more.available && (
        <LoadOlder
          loading={more.loading}
          failed={more.failed}
          failedMessage="Could not load older rates. Try again."
          onLoad={onLoadMore}
        />
      )}
    </>
  );
}

function TariffRow({
  tariff,
  currency,
  canWrite,
  onRemoved,
}: {
  tariff: Tariff;
  currency: string;
  canWrite: boolean;
  onRemoved: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function remove(): Promise<void> {
    setRemoving(true);
    setProblem(null);
    try {
      await api.deleteTariff(tariff.id);
      onRemoved();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not remove the rate.");
      setRemoving(false);
    }
  }

  return (
    <li className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-line p-4">
      <div className="flex min-w-0 flex-col gap-1">
        <Terms tariff={tariff} currency={currency} />
        <p role="alert" className="text-xs text-danger-ink">
          {problem ?? ""}
        </p>
      </div>

      {canWrite && (
        <RemoveControls
          confirming={confirming}
          removing={removing}
          onAsk={() => {
            setConfirming(true);
          }}
          onConfirm={() => {
            void remove();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      )}
    </li>
  );
}

/** When a rate starts, what a kWh costs under it, and how VAT was applied. */
function Terms({ tariff, currency }: { tariff: Tariff; currency: string }) {
  const vat = tariff.vatInclusive
    ? "The rate includes VAT."
    : `A rate of ${formatAmount(tariff.ratePerKwh)} plus ${String(tariff.vatPercent)}% VAT.`;
  const span =
    tariff.effectiveTo === null
      ? "The latest rate."
      : `Replaced from ${formatLocalDate(tariff.effectiveTo)}.`;
  return (
    <>
      <p className="text-sm font-medium text-ink">From {formatLocalDate(tariff.effectiveFrom)}</p>
      <p className="tabular text-sm text-ink">
        {`${formatAmount(tariff.pricePerKwh)} ${currency} per kWh`}
      </p>
      <p className="text-xs text-ink-muted">{`${vat} ${span}`}</p>
    </>
  );
}

/**
 * Removing re-prices history, so it asks once. Two buttons rather than a
 * browser `confirm()`, which the keyboard and screen-reader paths handle
 * worse and a test cannot drive without stubbing the window.
 */
function RemoveControls({
  confirming,
  removing,
  onAsk,
  onConfirm,
  onCancel,
}: {
  confirming: boolean;
  removing: boolean;
  onAsk: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!confirming) {
    return (
      <button className={secondaryButtonClass} type="button" onClick={onAsk}>
        Remove
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-ink-muted">The days it priced will be re-priced.</span>
      <button className={buttonClass} type="button" disabled={removing} onClick={onConfirm}>
        {removing ? "Removing…" : "Remove this rate"}
      </button>
      <button className={secondaryButtonClass} type="button" disabled={removing} onClick={onCancel}>
        Keep it
      </button>
    </div>
  );
}
